<template>
  <DateModal
    :value="isDialogOpened"
    @input="handleCloseModal"
    @click:submit="applySelectedDate"
  />
</template>

<script setup lang="ts">
import { watch, computed } from 'vue'
import { dispatchCustomEventGlobally } from 'www/utils'
import I18NextVue from 'i18next'
import {
  format,
  parseISO,
  isSameDay,
} from 'date-fns'
import getDateLabel from 'www/vue_components/common/TheFilters/doctors/components/DateFilters/functions/getDateLabel'
import useChipsStore from 'www/vue_components/common/TheFilters/doctors/stores/useChipsStore'
import useFiltersStore from 'www/vue_components/common/TheFilters/doctors/stores/useFiltersStore'
import useDialogManager from 'www/vue_components/common/mixins/DialogManager/useDialogManager'
import updateDoctorListPage from 'www/vue_components/common/TheFilters/doctors/functions/updateDoctorListPage'
import DateModal from 'www/vue_components/common/TheFilters/doctors/components/DateFilters/components/DateModal.vue'
import { EVENTS as SNACKBAR_EVENTS } from 'www/vue_components/common/core/SnackbarNotification/constants'
import { WEEK_DATES } from 'www/vue_components/common/TheFilters/doctors/constants'

const chipsStore = useChipsStore()
const filtersStore = useFiltersStore()

const { t, language } = I18NextVue
const options = computed(() => ({ ns: language }))

const { isDialogOpened, openDialog, closeDialog } = useDialogManager('DateFilters')

// Проверяем, что диапазон включает только сегодняшнюю дату
const isRangeOnlyToday = computed(() => {
  const [start, end] = filtersStore.filters.dateRange || []

  return (
    start && end
    && isSameDay(parseISO(start), parseISO(end))
    && isSameDay(parseISO(start), WEEK_DATES.today)
  )
})

// Проверяем, что диапазон включает только завтрашнюю дату
const isRangeOnlyTomorrow = computed(() => {
  const [start, end] = filtersStore.filters.dateRange || []

  return (
    start && end
    && isSameDay(parseISO(start), parseISO(end))
    && isSameDay(parseISO(start), WEEK_DATES.tomorrow)
  )
})

// Проверяем, что диапазон включает только выходные дни
const isRangeOnlyNearestWeekends = computed(() => {
  const [start, end] = filtersStore.filters.dateRange || []

  return (
    start && end
    && isSameDay(parseISO(start), WEEK_DATES.saturday)
    && isSameDay(parseISO(end), WEEK_DATES.sunday)
  )
})

const isActiveFastChips = computed(() => (
  isRangeOnlyToday.value
  || isRangeOnlyTomorrow.value
  || (isRangeOnlyNearestWeekends.value && !WEEK_DATES.isTodaySaturday)
))
const isActiveCommonDate = computed(() => (
  !!(filtersStore.filters.dateRange || filtersStore.filters.timesOfDay)
))
const isVisibleCommonDate = computed(() => (
  (!isActiveFastChips.value && isActiveCommonDate.value)
  || !!filtersStore.filters.timesOfDay
))

watch(
  () => filtersStore.filters.dateRange || filtersStore.filters.timesOfDay,
  () => {
    chipsStore.updateTodayChip({
      name: t('Сегодня', options.value),
      isActive: !!isRangeOnlyToday.value,
      isVisible: !isVisibleCommonDate.value,
      onClick: async () => {
        if (isRangeOnlyToday.value) {
          openDialog()
          return
        }

        const today = format(WEEK_DATES.today, 'yyyy-MM-dd')

        filtersStore.updateFilters({
          dateRange: [today, today],
        })

        await applySelectedDate()
      },
      onReset,
    })

    chipsStore.updateTomorrowChip({
      name: t('Завтра', options.value),
      isActive: !!isRangeOnlyTomorrow.value,
      isVisible: !isVisibleCommonDate.value,
      onClick: async () => {
        if (isRangeOnlyTomorrow.value) {
          openDialog()
          return
        }

        const tomorrow = format(WEEK_DATES.tomorrow, 'yyyy-MM-dd')

        filtersStore.updateFilters({
          dateRange: [tomorrow, tomorrow],
        })

        await applySelectedDate()
      },
      onReset,
    })

    chipsStore.updateWeekendChip({
      name: t('На выходных', options.value),
      isActive: !!isRangeOnlyNearestWeekends.value,
      isVisible: !isVisibleCommonDate.value && !WEEK_DATES.isTodaySaturday,
      onClick: async () => {
        if (isRangeOnlyNearestWeekends.value) {
          openDialog()
          return
        }

        const saturday = format(WEEK_DATES.saturday, 'yyyy-MM-dd')
        const sunday = format(WEEK_DATES.sunday, 'yyyy-MM-dd')

        filtersStore.updateFilters({
          dateRange: [saturday, sunday],
        })

        await applySelectedDate()
      },
      onReset,
    })

    chipsStore.updateDateChip({
      name: getDateLabel({ dateRange: filtersStore.filters.dateRange, timesOfDay: filtersStore.filters.timesOfDay }),
      isActive: isActiveCommonDate.value,
      dropdown: !isActiveCommonDate.value, // скрыт поле
      isVisible: isVisibleCommonDate.value,
      onClick: openDialog,
      onReset,
    })
  },
  { immediate: true },
)

async function onReset() {
  filtersStore.updateFilters({ dateRange: undefined, timesOfDay: undefined })

  await applySelectedDate()

  dispatchCustomEventGlobally(SNACKBAR_EVENTS.open, {
    upper: true,
    message: t('Фильтр сброшен', options.value),
  })
}

async function applySelectedDate() {
  closeDialog()
  filtersStore.applyUnsavedFilters()
  await updateDoctorListPage()
}

function handleCloseModal() {
  closeDialog()
  filtersStore.resetUnsavedFilters()
}
</script>
